import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
const PROPERTY_LAT = -37.8438;
const PROPERTY_LNG = 145.001;
const PROPERTY_LABEL = '500 Toorak Road, Toorak VIC 3142';
type Amenity = {
  name: string;
  lat: number;
  lng: number;
  distance: string | null;
};
const AMENITIES: Amenity[] = [{
  name: 'Toorak Village',
  lat: -37.841195,
  lng: 145.009031,
  distance: '500m'
}, {
  name: 'Fawkner Park',
  lat: -37.841538,
  lng: 144.982141,
  distance: '950m'
}, {
  name: 'Melbourne Grammar',
  lat: -37.833414,
  lng: 144.975984,
  distance: '1.2km'
}, {
  name: 'Scotch College',
  lat: -37.834218,
  lng: 145.029148,
  distance: '3.3km'
}, {
  name: 'Kooyong Tennis Club',
  lat: -37.839073,
  lng: 145.03198,
  distance: null
}];
export function ToorakRoadMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    // Inject Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    // Wait for CSS to load
    setTimeout(() => {
      if (!mapRef.current) return;
      // On touch devices a one-finger drag would hijack page scrolling, so
      // panning is disabled there — the pins, pills and zoom controls still
      // carry the location context.
      const isTouch = L.Browser.mobile;
      const map = L.map(mapRef.current, {
        center: [PROPERTY_LAT, PROPERTY_LNG],
        zoom: isTouch ? 13 : 14,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        dragging: !isTouch,
        tap: false
      });
      // Esri Light Gray Canvas — clean minimal tiles, no API key required.
      // (CARTO's Positron endpoint now stamps "API KEY REQUIRED" across every
      // tile unless you hold an account key.) The base carries landforms and
      // roads; the reference layer puts the street and suburb labels on top.
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16
      }).addTo(map);
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16
      }).addTo(map);
      // Add zoom control to bottom right
      L.control.zoom({
        position: 'bottomright'
      }).addTo(map);
      // Custom property marker
      const propertyIcon = L.divIcon({
        className: 'property-marker',
        html: `
          <div style="
            width: 16px;
            height: 16px;
            background: #E4002B;
            border-radius: 50%;
            border: 3px solid #fff;
            box-shadow: 0 2px 8px rgba(228,0,43,0.35);
          "></div>
        `,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      L.marker([PROPERTY_LAT, PROPERTY_LNG], {
        icon: propertyIcon
      }).addTo(map).bindPopup(`<div style="font-family: 'Playfair Display', serif; text-align: center; padding: 4px 0;">
            <strong style="font-size: 13px; letter-spacing: 0.05em;">${PROPERTY_LABEL}</strong>
          </div>`, {
        closeButton: false,
        className: 'property-popup'
      });
      // 15-minute walking radius (~1200m at 80m/min)
      L.circle([PROPERTY_LAT, PROPERTY_LNG], {
        radius: 1200,
        color: '#02243F',
        weight: 1.5,
        opacity: 0.5,
        fillColor: '#D9E3EC',
        fillOpacity: 0.5,
        dashArray: '6, 6',
        interactive: false
      }).addTo(map);
      // Walking radius label
      const walkLabelIcon = L.divIcon({
        className: 'walk-label',
        html: `<span style="
          font-family: 'Montserrat', sans-serif;
          font-size: 9px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #02243F;
          white-space: nowrap;
          background: rgba(255,255,255,0.85);
          padding: 2px 6px;
        ">15 min walk</span>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0]
      });
      // Place label at the top of the circle (1200m north)
      const labelLat = PROPERTY_LAT + 1200 / 111320;
      L.marker([labelLat, PROPERTY_LNG], {
        icon: walkLabelIcon,
        interactive: false
      }).addTo(map);
      // Amenity markers
      const amenityIcon = L.divIcon({
        className: 'amenity-marker',
        html: `
          <div style="
            width: 12px;
            height: 12px;
            background: #02243F;
            border-radius: 50%;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(2,36,63,0.3);
          "></div>
        `,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      AMENITIES.forEach(poi => {
        L.marker([poi.lat, poi.lng], {
          icon: amenityIcon
        }).addTo(map).bindPopup(`<div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 2px 0;">
              <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #737373;">${poi.name}</span>
              ${poi.distance ? `<br/><span style="font-size: 10px; color: #999;">${poi.distance}</span>` : ''}
            </div>`, {
          closeButton: false
        });
      });
      // Attribution in bottom-left, subtle
      L.control.attribution({
        position: 'bottomleft',
        prefix: false
      }).addAttribution('Tiles © <a href="https://www.esri.com/" style="color:#aaa;">Esri</a>').addTo(map);
      mapInstanceRef.current = map;
      // Handle resize
      const handleResize = () => map.invalidateSize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, 100);
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  return <section id="location" className="relative bg-white border-y border-black/5" data-id="element-108" data-mp="kkm3mshxsz0w">
      {/* Section header */}
      <div className="px-6 md:px-12 pt-16 md:pt-32 pb-10 md:pb-16" data-id="element-109" data-mp="ft43u7rnh4hi">
        <div className="max-w-7xl mx-auto" data-id="element-110" data-mp="austzx1cutza">
          <div className="flex items-center gap-4 mb-6 md:mb-8" data-id="element-111" data-mp="atzvtkggpcsr">
            <div className="w-8 h-[1px] bg-brand-navy" data-id="element-112" data-mp="189x223btehbv"></div>
            <span className="font-body text-[10px] text-brand-navy tracking-widest uppercase" data-id="element-113" data-mp="18c9pux497lwu">
              The Location
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-6" data-id="element-114" data-mp="1aveego4tk3pv">
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.1]" data-id="element-115" data-mp="1fur2vdbc9iuk">
              Toorak's Most <br data-id="element-116" data-mp="1krr8jn4a0jl5" />
              <span className="italic text-brand-navy" data-id="element-117" data-mp="1kr7syd23m7aa">Coveted Address.</span>
            </h2>
            <div className="flex flex-wrap gap-2" data-id="element-118" data-mp="1ptgmpbmspddg">
              {AMENITIES.map(poi => <span key={poi.name} className="font-body text-[9px] md:text-[10px] tracking-loose md:tracking-widest uppercase text-brand-navy border border-brand-navy/20 px-2.5 md:px-3 py-1.5 whitespace-nowrap" data-id="element-119" data-mp="1tspguhlqzd9j">
                  {poi.distance ? `${poi.name} · ${poi.distance}` : poi.name}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div ref={mapRef} className="w-full h-[58svh] min-h-[360px] md:h-[65vh] md:min-h-[500px]" data-id="element-120" data-mp="2his32h2zdp08" />

      {/* Leaflet overrides */}
      <style dangerouslySetInnerHTML={{
      __html: `
        .leaflet-container {
          font-family: 'Montserrat', sans-serif;
        }
        .leaflet-control-zoom {
          border: 1px solid rgba(0,0,0,0.1) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .leaflet-control-zoom a {
          color: #000 !important;
          background: #fff !important;
          border-radius: 0 !important;
          border-bottom: 1px solid rgba(0,0,0,0.1) !important;
          width: 44px !important;
          height: 44px !important;
          line-height: 44px !important;
          font-size: 18px !important;
        }
        .leaflet-control-zoom a:last-child {
          border-bottom: none !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          padding: 8px 12px !important;
        }
        .leaflet-popup-tip {
          box-shadow: none !important;
        }
        .leaflet-control-attribution {
          font-size: 9px !important;
          background: transparent !important;
          color: #ccc !important;
        }
        .leaflet-control-attribution a {
          color: #aaa !important;
        }
      `
    }} data-id="element-121" data-mp="32s6vhtu6uo0g" />
    </section>;
}